import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { toast } from "sonner";
import { Bell, Lock, User, Shield } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [tempToken, setTempToken] = useState("");
  const [otpCode, setOtpCode] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, { username, password });
      if (response.data.requires_2fa) {
        // 2FA aktiviert - zum 2FA Schritt
        setTempToken(response.data.temp_token);
        setStep(2);
        toast.success("Passwort verifiziert");
      } else {
        // 2FA deaktiviert - direkt einloggen
        localStorage.setItem("token", response.data.access_token);
        toast.success("Erfolgreich eingeloggt");
        navigate("/dashboard");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Login fehlgeschlagen");
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async (code) => {
    if (code.length !== 6) return;
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/verify-2fa`, {
        temp_token: tempToken,
        code: code
      });
      localStorage.setItem("token", response.data.access_token);
      toast.success("Erfolgreich eingeloggt");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Ungültiger Code");
      setOtpCode("");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-4">
      {/* Background image with overlay */}
      <div 
        className="fixed inset-0 bg-cover bg-center opacity-20"
        style={{ backgroundImage: "url('https://images.pexels.com/photos/3115127/pexels-photo-3115127.jpeg')" }}
      />
      <div className="fixed inset-0 bg-black/80" />

      <Card className="w-full max-w-md bg-[#0A0A0A] border-[#262626] relative z-10" data-testid="login-card">
        <CardHeader className="text-center pb-2">
          <div className="mx-auto w-16 h-16 bg-[#3B82F6]/10 rounded-full flex items-center justify-center mb-4">
            <Bell className="w-8 h-8 text-[#3B82F6]" />
          </div>
          <CardTitle className="text-2xl font-bold text-white font-mono">
            Push Dashboard
          </CardTitle>
          <CardDescription className="text-[#737373]">
            {step === 1 ? "Melde dich an" : "2FA Verifizierung"}
          </CardDescription>
        </CardHeader>

        <CardContent>
          {step === 1 ? (
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-[#737373]">Benutzername</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="username"
                    data-testid="username-input"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-[#171717] border-[#262626] text-white placeholder:text-[#737373] font-mono"
                    placeholder="admin"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-[#737373]">Passwort</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#737373]" />
                  <Input
                    id="password"
                    type="password"
                    data-testid="password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 bg-[#171717] border-[#262626] text-white placeholder:text-[#737373]"
                    placeholder="••••••••"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit-btn"
                disabled={loading}
                className="w-full h-12 bg-[#3B82F6] text-black hover:bg-[#3B82F6]/90 font-mono font-bold btn-primary"
              >
                {loading ? (
                  <div className="w-5 h-5 border-2 border-black border-t-transparent rounded-full animate-spin" />
                ) : (
                  "Anmelden"
                )}
              </Button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="text-center">
                <div className="mx-auto w-12 h-12 bg-[#3B82F6]/10 rounded-full flex items-center justify-center mb-4">
                  <Shield className="w-6 h-6 text-[#3B82F6]" />
                </div>
                <p className="text-[#737373] text-sm">
                  Gib den 6-stelligen Code aus deiner Authenticator App ein
                </p>
              </div>

              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpCode}
                  onChange={(value) => {
                    setOtpCode(value);
                    if (value.length === 6) {
                      handleVerify2FA(value);
                    }
                  }}
                  data-testid="otp-input"
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} className="bg-[#171717] border-[#262626] text-white font-mono text-lg" />
                    <InputOTPSlot index={1} className="bg-[#171717] border-[#262626] text-white font-mono text-lg" />
                    <InputOTPSlot index={2} className="bg-[#171717] border-[#262626] text-white font-mono text-lg" />
                    <InputOTPSlot index={3} className="bg-[#171717] border-[#262626] text-white font-mono text-lg" />
                    <InputOTPSlot index={4} className="bg-[#171717] border-[#262626] text-white font-mono text-lg" />
                    <InputOTPSlot index={5} className="bg-[#171717] border-[#262626] text-white font-mono text-lg" />
                  </InputOTPGroup>
                </InputOTP>
              </div>

              {loading && (
                <div className="flex justify-center">
                  <div className="w-6 h-6 border-2 border-[#3B82F6] border-t-transparent rounded-full animate-spin" />
                </div>
              )}

              <Button
                variant="ghost"
                onClick={() => { setStep(1); setOtpCode(""); }}
                className="w-full text-[#737373] hover:text-white"
                data-testid="back-to-login-btn"
              >
                Zurück zum Login
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
